import { useEffect, useState } from "react";
import { signIn } from "next-auth/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart } from "@fortawesome/free-solid-svg-icons";

// Single reaction: GREAT (Like). Other reactions removed.
const LIKE_TYPE = "GREAT" as const;
type ReactionType = typeof LIKE_TYPE;

type ReactionBarProps = {
  articleId: string;
  token?: string | null;
  /** Compact rendering for in-header placement (no helper label, no border block). */
  compact?: boolean;
};

export default function ReactionBar({ articleId, token, compact = false }: ReactionBarProps) {
  const [count, setCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchReactions = async () => {
      const res = await fetch(
        `${process.env.BACKEND_BASE_URL}/contents/${articleId}/reactions`,
      );
      const data = await res.json();
      if (!active || !data?.data) return;
      const counts = data.data.counts || {};
      setCount(counts[LIKE_TYPE] || 0);

      if (!token) return;
      const meRes = await fetch(`${process.env.BACKEND_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const meData = await meRes.json();
      if (!active || meData?.success !== true) return;
      const myId = meData.data?._id;
      const mine = data.data.records.find(
        (r: { ownerId: string; reactionType: string }) =>
          r.ownerId === myId && r.reactionType === LIKE_TYPE,
      );
      setLiked(!!mine);
    };
    fetchReactions();
    return () => {
      active = false;
    };
  }, [articleId, token]);

  const handleToggle = async () => {
    if (!token) {
      signIn();
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Optimistic update
    const wasLiked = liked;
    setLiked(!wasLiked);
    setCount((c) => Math.max(0, c + (wasLiked ? -1 : 1)));

    try {
      const res = await fetch(
        `${process.env.BACKEND_BASE_URL}/contents/${articleId}/reaction`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reactionType: LIKE_TYPE }),
        },
      );
      const data = await res.json();
      if (data?.data) {
        const counts = data.data.counts || {};
        setCount(counts[LIKE_TYPE] || 0);
        const myRes = await fetch(`${process.env.BACKEND_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const myData = await myRes.json();
        if (myData?.success === true) {
          const myId = myData.data?._id;
          const mine = data.data.records.find(
            (r: { ownerId: string; reactionType: string }) =>
              r.ownerId === myId && r.reactionType === LIKE_TYPE,
          );
          setLiked(!!mine);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const button = (
    <button
      onClick={handleToggle}
      disabled={isSubmitting}
      aria-label={liked ? "Unlike" : "Like"}
      aria-pressed={liked}
      className={`inline-flex h-9 items-center gap-2 px-3.5 font-display text-sm font-semibold transition-colors duration-150 select-none
        ${
          liked
            ? "bg-signal text-white hover:bg-[#0047CC]"
            : "bg-theme-surface text-theme-text hover:bg-signal hover:text-white"
        }
        ${isSubmitting ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}
      `}
    >
      {/* On Air: no emoji. The plate turns signal blue when liked. */}
      <FontAwesomeIcon icon={faHeart} className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="leading-none">Like</span>
      {count > 0 && (
        <span className="tabular-nums leading-none">
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );

  if (compact) {
    if (count === 0 && !token) return null;
    return button;
  }

  if (count === 0 && !token) return null;

  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-sm font-medium leading-5 text-theme-text-secondary">
        Did you like this article?
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">{button}</div>
    </div>
  );
}

// Compact summary for article cards — kept for backwards compatibility.
export function ReactionSummary({
  counts,
}: {
  counts: Partial<Record<string, number>>;
}) {
  const likes = counts?.[LIKE_TYPE] ?? 0;
  if (!likes) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-theme-text-secondary" aria-label={`Likes: ${likes.toLocaleString()}`}>
      <FontAwesomeIcon icon={faHeart} className="h-[10px] w-[10px] text-signal dark:text-[#8DB4FF]" aria-hidden="true" />
      <span className="tabular-nums">{likes.toLocaleString()}</span>
    </span>
  );
}
